'use client';
import React from 'react';
import * as format from '@/lib/format';
import EntryStatusTag from './EntryStatusTag';
import * as Styled from './styled';

import { TEntryDataType } from '@/types';

import { IRepositoryCompareEntry } from '@/lib/repositoryCompareApi';
import { Row, Card } from '@/ui/primatives';
import Tag from '@/ui/Tag';
import Badge from '@/ui/Badge';
import Link from 'next/link';

import * as EntryMetaData from './EntryMetaData';
import * as DiffSummary from './DiffSummary';
import * as Icon from '@/ui/Icon';

const FilesizeTag: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <Tag
      small
      style={{ minWidth: 75, justifyContent: 'space-between' }}
    >
      {children}
    </Tag>
  );
};

const DiffEntry: React.FC<{
  namespace: string;
  repositoryName: string;
  entry: IRepositoryCompareEntry;
  onFolderSelect?: (path: string) => void;
}> = ({ namespace, repositoryName, entry, onFolderSelect }) => {
  const { status, filename, data_type, head_resource, base_resource, size } = entry;
  const isModified = status === 'modified';
  const isAdded = status === 'added';
  const linkResource = status === 'removed' && base_resource ? base_resource : head_resource;

  const directorySizeChange =
    entry.data_type === 'dir' && (entry.head_entry?.size || 0) - (entry.base_entry?.size || 0);

  const fileSizeChange = entry.data_type !== 'dir' && (entry.head_entry?.size || 0) - (entry.base_entry?.size || 0);

  const filePath = entry.is_dir
    ? `/${namespace}/${repositoryName}/dir/${linkResource?.version}/${linkResource?.path}`
    : `/${namespace}/${repositoryName}/file/${linkResource?.version}/${linkResource?.path}`;

  const baseHead = `${entry.base_resource?.version}..${entry.head_resource?.version}`;
  const fileComparePath = `/${namespace}/${repositoryName}/compare/file/${baseHead}/${entry.filename}`;

  const targetPath = isModified ? fileComparePath : filePath;

  return (
    // <Card compressed style={{ padding: 10, background: 'none' || statusColor[status] }}>
    <Card
      compressed
      style={{ padding: 10, background: 'none' }}
    >
      <Row
        gap={10}
        key={filename}
        alignItems="center"
      >
        <EntryStatusTag status={entry.status} />
        {data_type === 'dir' ? (
          <Icon.GithubFolder style={{ color: 'var(--color-secondary-mediumGray)' }} />
        ) : (
          <Badge
            variant={data_type as TEntryDataType}
            style={{ minWidth: 75 }}
          >
            {data_type}
          </Badge>
        )}
        <div style={{ flex: 1 }}>
          {onFolderSelect && data_type === 'dir' ? (
            <Styled.FolderLink onClick={() => onFolderSelect(filename)}> {filename}</Styled.FolderLink>
          ) : (
            <Link
              className="mono hover-link"
              href={targetPath}
            >
              {filename}
            </Link>
          )}
        </div>

        <DiffSummary.Folder entry={entry} />
        <DiffSummary.Tabular entry={entry} />

        <EntryMetaData.Audio entry={entry} />
        <EntryMetaData.Image entry={entry} />
        <EntryMetaData.Video entry={entry} />
        <EntryMetaData.Text entry={entry} />
        <EntryMetaData.Tabular entry={entry} />

        {isModified ? (
          <FilesizeTag>
            <span>Δ</span>
            <span>{format.humanFileSize(directorySizeChange || fileSizeChange || size)}</span>
          </FilesizeTag>
        ) : isAdded ? (
          <FilesizeTag>
            <span></span>+{format.humanFileSize(directorySizeChange || fileSizeChange || size)}
          </FilesizeTag>
        ) : (
          <FilesizeTag>
            <span></span>
            {format.humanFileSize(directorySizeChange || fileSizeChange || size)}
          </FilesizeTag>
        )}
      </Row>
      {/* <DirectoryFileTypeChangeSet entry={entry} /> */}
    </Card>
  );
};

export default DiffEntry;
